import { ICONS } from '@frontend/icons';
import React from 'react';

import { useObjectBadgeOverride } from './object-badge-override.context';
import { useObjectBadge } from './object-badge.controller';

/**
 * @deprecated For platform entity detail navigation use PlatformObjectBadge / openPlatformDetail instead.
 * Kept for admin and unmigrated callers; behavior unchanged.
 * When ObjectBadgeOverrideProvider is mounted (admin), renders the app override instead.
 */
export interface ObjectBadgeProps {
    id?: string;
    entityType: string;
    display?: string;
    className?: string;
    disableClick?: boolean;
    navigateOnDisplayName?: boolean;
    hideIcon?: boolean;
    queryParamNavigation?: boolean;
    /**
     * Optional parent IDs to include in the query params when opening the detail modal.
     * These will be prefixed with 'parent_' in the URL (e.g., parent_spot_id=123).
     */
    parentIds?: Record<string, string>;
    clearable?: {
        onClear: () => void;
    };
    overwriteDisplayMap?: (entity: any) => string;
    styles?: {
        icon?: string;
    };
}

/**
 * @deprecated For platform entity detail navigation use PlatformObjectBadge / openPlatformDetail instead.
 * Kept for admin and unmigrated callers; behavior unchanged.
 * When ObjectBadgeOverrideProvider is mounted (admin), renders the app override instead.
 */
export const ObjectBadge = (props: ObjectBadgeProps) => {
    const override = useObjectBadgeOverride();
    if (override) {
        return override({
            id: props.id,
            entityType: props.entityType,
            display: props.display,
            className: props.className,
            disableClick: props.disableClick,
            hideIcon: props.hideIcon,
            clearable: props.clearable,
            overwriteDisplayMap: props.overwriteDisplayMap,
            styles: props.styles
        });
    }

    return <LegacyObjectBadge {...props} />;
};

const LegacyObjectBadge = (props: ObjectBadgeProps) => {
    const { repository, utils } = useObjectBadge(props);
    const styles = {
        hover: 'h-6 px-1 bg-brand-primary rounded-sm inline-flex justify-center items-center text-center gap-1',
        default:
            'px-1 justify-start items-center text-brand-primary text-sm font-semibold rounded-sm inline-flex items-center gap-1 hover:bg-brand-primary hover:text-background-surface hover:cursor-pointer',
        disabled: 'h-6 px-1 text-brand-muted rounded-sm inline-flex justify-center items-center gap-1 cursor-default'
    };
    return (
        <span
            onClick={(e) => {
                e.stopPropagation();
                if (!props.disableClick && !repository.disabled && repository.isPlatform) {
                    if (repository.object && !props.display && !props.queryParamNavigation) {
                        repository.adminOnClick(e, utils.getNavigateString(repository.object));
                    } else if (props.display || props.queryParamNavigation) {
                        repository.onClick(e);
                    }
                }
            }}
            id={'badge-' + props.id}
            className={
                props.className
                    ? props.className
                    : `${repository.disabled ? styles.disabled : props.navigateOnDisplayName ? 'text-brand-primary/70' : styles.default}`
            }>
            {!props.hideIcon &&
                (repository.icon ? (
                    <repository.icon
                        className={
                            props.styles?.icon
                                ? props.styles.icon
                                : `size-4 ${repository.disabled ? 'text-primary/50' : ''} ${props.navigateOnDisplayName ? 'hover:text-primary/70' : ''}`
                        }
                    />
                ) : (
                    <span className='placeholder-glow'>
                        <span className='placeholder'>loading</span>
                    </span>
                ))}

            {repository.displayName || props.display === '' ? (
                <span className='flex flex-1 items-center'>{repository.displayName}</span>
            ) : (
                <span className='placeholder-glow'>
                    <span className='placeholder'>loading</span>
                </span>
            )}
            {props.clearable && (
                <span
                    className='cursor-pointer'
                    onClick={props.clearable.onClear}>
                    <ICONS.BUTTON.CLOSE_2 className='text-background-surface size-4' />
                </span>
            )}
        </span>
    );
};
